import api from './api';

const caseService = {
  // Cases
  getCases: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/cases/?${queryString}`);
    return response.data;
  },

  // Same request, plus the paging totals the server now sends as headers. Kept
  // separate from getCases so the eight other callers of this endpoint keep
  // their bare-list contract untouched.
  //
  // Read the header names LOWERCASE: axios normalises response headers to
  // lowercase, and a capitalised lookup returns undefined — which would render
  // "no total" while the server was sending one correctly.
  getCasesPaged: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/cases/?${queryString}`);
    const total = Number(response.headers?.['x-total-count']);
    return {
      cases: response.data,
      total: Number.isFinite(total) ? total : null,
      limit: Number(response.headers?.['x-limit']) || null,
      offset: Number(response.headers?.['x-offset']) || 0,
    };
  },

  getCase: async (caseId) => {
    const response = await api.get(`/cases/${caseId}`);
    return response.data;
  },

  createCase: async (caseData) => {
    const response = await api.post('/cases/', caseData);
    return response.data;
  },

  updateCase: async (caseId, caseData) => {
    const response = await api.patch(`/cases/${caseId}`, caseData);
    return response.data;
  },

  deleteCase: async (caseId) => {
    const response = await api.delete(`/cases/${caseId}`);
    return response.data;
  },

  // Delete Case (soft or hard)
  deleteCaseSoft: async (caseId) => {
    const response = await api.delete(`/cases/${caseId}`);
    return response.data;
  },

  deleteCasePermanent: async (caseId) => {
    const response = await api.delete(`/cases/${caseId}?permanent=true`);
    return response.data;
  },

  // Restore deleted case
  restoreCase: async (caseId) => {
    const response = await api.post(`/cases/${caseId}/restore`);
    return response.data;
  },

  // List deleted cases
  getDeletedCases: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/cases/deleted?${queryString}`);
    return response.data;
  },

  // Case Updates
  getCaseUpdates: async (caseId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/cases/${caseId}/updates?${queryString}`);
    return response.data;
  },

  addCaseUpdate: async (caseId, updateData) => {
    const response = await api.post(`/cases/${caseId}/updates`, updateData);
    return response.data;
  },

  getCaseCalendarEvents: async (caseId) => {
    const response = await api.get(`/cases/${caseId}/calendar-events`);
    return response.data;
  },

  // Time Entries
  getTimeEntries: async (caseId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/cases/${caseId}/time-entries?${queryString}`);
    return response.data;
  },

  addTimeEntry: async (caseId, entryData) => {
    const response = await api.post(`/cases/${caseId}/time-entries`, entryData);
    return response.data;
  },

  updateTimeEntry: async (caseId, entryId, entryData) => {
    const response = await api.patch(`/cases/${caseId}/time-entries/${entryId}`, entryData);
    return response.data;
  },

  deleteTimeEntry: async (caseId, entryId) => {
    const response = await api.delete(`/cases/${caseId}/time-entries/${entryId}`);
    return response.data;
  },

  // Tasks
  getTasks: async (caseId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/cases/${caseId}/tasks?${queryString}`);
    return response.data;
  },

  createTask: async (caseId, taskData) => {
    const response = await api.post(`/cases/${caseId}/tasks`, taskData);
    return response.data;
  },

  updateTask: async (caseId, taskId, taskData) => {
    const response = await api.patch(`/cases/${caseId}/tasks/${taskId}`, taskData);
    return response.data;
  },

  // Documents
  getDocuments: async (caseId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/cases/${caseId}/documents?${queryString}`);
    return response.data;
  },

  uploadDocument: async (caseId, formData) => {
    const response = await api.post(`/cases/${caseId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadDocument: async (caseId, docId) => {
    const response = await api.get(`/cases/${caseId}/documents/${docId}/download`, {
      responseType: 'blob',
    });
    return response;
  },

  // Contacts — use the canonical /contacts endpoint. `/cases/contacts` is dead:
  // it is shadowed by GET /cases/{case_id} (declared earlier), so it resolves
  // case_id="contacts" and 404s "Case not found" — which was blanking the
  // discovery Produce & Deliver tab on every case.
  getContacts: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/contacts/?${queryString}`);
    return response.data;
  },

  createContact: async (contactData) => {
    const response = await api.post('/cases/contacts', contactData);
    return response.data;
  },

  // Expenses
  getExpenses: async (caseId) => {
    const response = await api.get(`/cases/${caseId}/expenses`);
    return response.data;
  },

  addExpense: async (caseId, expenseData) => {
    const response = await api.post(`/cases/${caseId}/expenses`, expenseData);
    return response.data;
  },

  // Statistics
  getDashboardStats: async () => {
    const response = await api.get('/cases/stats/dashboard');
    return response.data;
  },

  getBillingStats: async () => {
    const response = await api.get('/cases/stats/billing');
    return response.data;
  },

  // UTBMS Codes (for LEDES e-billing compliance)
  getUtbmsCodes: async (codeType = null) => {
    const params = codeType ? `?code_type=${codeType}` : '';
    const response = await api.get(`/cases/utbms-codes${params}`);
    return response.data;
  },

  // Prepaid Hours
  getPrepaidInfo: async (caseId) => {
    const response = await api.get(`/cases/${caseId}/prepaid`);
    return response.data;
  },

  purchasePrepaidHours: async (caseId, purchaseData) => {
    const response = await api.post(`/cases/${caseId}/prepaid/purchase`, purchaseData);
    return response.data;
  },

  deductPrepaidHours: async (caseId, deductData) => {
    const response = await api.post(`/cases/${caseId}/prepaid/deduct`, deductData);
    return response.data;
  },

  // Unbilled Items (for invoice generation)
  getUnbilledItems: async (caseId) => {
    const response = await api.get(`/cases/${caseId}/unbilled`);
    return response.data;
  },
};

export default caseService;
